import { Image } from 'expo-image';
import React from 'react';
import { View } from 'react-native';

import { Checkbox, type CheckboxProps, Text, XStack, useCheckbox } from '@fhs/ui';

import { optimizeSanityImageUri } from '../../utils';

import { BORDER_RADIUS, IMAGE_HEIGHT } from './constants';
import { ListItemUnavailableBadge } from './list-item-unavailable-badge';
import { listStyles } from './shared-styles';
import type { ListItemType } from './types';

type ListItemCheckboxProps = ListItemType & {
  onChange: CheckboxProps['onChange'];
  checked: CheckboxProps['checked'];
  unavailable?: CheckboxProps['unavailable'];
  disabled?: CheckboxProps['disabled'];
  softDisabled?: CheckboxProps['softDisabled'];
  onSoftDisabledPress?: CheckboxProps['onSoftDisabledPress'];
};

const IndicatorText = ({ indicatorText }: { indicatorText: string }) => {
  const { checked } = useCheckbox();
  return (
    <Text.Ui
      accessible={false}
      size="sm"
      numberOfLines={1}
      style={checked ? listStyles.indicatorTextSelected : listStyles.indicatorText}
    >
      {indicatorText}
    </Text.Ui>
  );
};

export function ListItemCheckbox({ minHeightStyle = 'static', ...props }: ListItemCheckboxProps) {
  const cmsImageAlt = props.image?.altText?.trim();
  const indicatorA11y =
    props.indicatorAccessibilityLabel?.trim() || props.indicatorText?.trim() || '';
  const checkboxLabel = [
    props.title,
    typeof props.subtitle === 'string' ? props.subtitle : '',
    indicatorA11y,
  ]
    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    .join(', ');

  return (
    <Checkbox
      onChange={props.onChange}
      checked={props.checked}
      label={checkboxLabel}
      unavailable={props.unavailable}
      softDisabled={props.softDisabled}
      onSoftDisabledPress={props.onSoftDisabledPress}
    >
      <Checkbox.Pressable
        style={[
          listStyles.item,
          minHeightStyle === 'static' && listStyles.minHeightStyleStatic,
          props.isLastItem && listStyles.lastItem,
        ]}
        hoveredStyle={listStyles.itemHover}
        borderTopLeftRadius={props.isFirstItem ? BORDER_RADIUS : 0}
        borderTopRightRadius={props.isFirstItem ? BORDER_RADIUS : 0}
        borderBottomLeftRadius={props.isLastItem ? BORDER_RADIUS : 0}
        borderBottomRightRadius={props.isLastItem ? BORDER_RADIUS : 0}
      >
        {props.image?.asset?.uri && (
          <Image
            contentFit="contain"
            style={[
              listStyles.image,
              (props.unavailable || props.disabled || props.softDisabled) &&
                listStyles.imageDisabled,
            ]}
            accessibilityLabel={cmsImageAlt || props.title}
            alt={cmsImageAlt || props.title}
            source={{ uri: optimizeSanityImageUri(props.image.asset.uri, IMAGE_HEIGHT) }}
            placeholder={{ blurhash: props.image.asset.blurHash }}
          />
        )}
        <View style={listStyles.titleTextContainer}>
          <View>
            <Text.Ui
              weight={props.titleFontWeight ?? 'semibold'}
              size="md"
              numberOfLines={1}
              style={
                (props.unavailable || props.disabled || props.softDisabled) &&
                listStyles.textDisabled
              }
            >
              {props.title}
            </Text.Ui>
          </View>
          {Boolean(props.subtitle) && (
            <View>
              <Text.Ui size="sm" numberOfLines={1} style={listStyles.subtitle}>
                {props.subtitle}
              </Text.Ui>
            </View>
          )}
        </View>

        {props.unavailable ? (
          <ListItemUnavailableBadge />
        ) : (
          <XStack style={listStyles.indicatorWithTextWrapper}>
            {Boolean(props.indicatorText) && (
              <IndicatorText indicatorText={props.indicatorText ?? ''} />
            )}
            <Checkbox.Indicator />
          </XStack>
        )}
      </Checkbox.Pressable>
    </Checkbox>
  );
}
